import React, { useState, useRef, useEffect } from 'react';
import { Send, X, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { useSession } from '../context/SessionContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LANGUAGES = [
  { key: 'hinglish', label: 'Hinglish' },
  { key: 'hindi', label: 'Hindi' },
  { key: 'english', label: 'English' },
];

const QUICK_QUESTIONS = [
  'What does my career look like this year?',
  'When is the best time for marriage?',
  'How is my health outlook?',
  'Will I gain wealth this period?',
];

const AskSageAnything = ({ domainId = 'health', onClose, userId: propUserId }) => {
  const { userId: sessionUserId } = useSession();
  // Prefer session userId (already guarded against ghost IDs). Fall back to
  // prop only if the caller explicitly passes a non-ghost value.
  const rawCandidate = sessionUserId || propUserId;
  const userId = (rawCandidate && !/^(native-|web-|session_|device_|taraanaya_device_)/.test(rawCandidate))
    ? rawCandidate
    : null;
  // ── PERSISTENCE: keep typed draft + chat history (with cached 3-lang audio)
  // alive across modal open/close, app refresh, login/logout. Storage is keyed
  // by userId so different users don't collide. Without this, every modal close
  // wipes the messages array → next "Hear" press has to RE-FETCH 3-language
  // audio from the server → user complaint "voice loads again and again".
  const SAGE_DRAFT_KEY = userId ? `sage_qa_draft_${userId}_${domainId}` : null;
  const SAGE_HISTORY_KEY = userId ? `sage_qa_history_${userId}_${domainId}` : null;
  const SAGE_SESSION_KEY = userId ? `sage_qa_session_${userId}_${domainId}` : null;

  const [messages, setMessages] = useState(() => {
    if (!SAGE_HISTORY_KEY) return [];
    try {
      const raw = localStorage.getItem(SAGE_HISTORY_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState(() => {
    if (!SAGE_DRAFT_KEY) return '';
    return localStorage.getItem(SAGE_DRAFT_KEY) || '';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLang, setSelectedLang] = useState('hinglish');
  const [sessionId, setSessionId] = useState(() => {
    if (!SAGE_SESSION_KEY) return null;
    return localStorage.getItem(SAGE_SESSION_KEY) || null;
  });
  const [playingAudio, setPlayingAudio] = useState(null);
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Persist draft on every change so reopening modal restores typed text
  useEffect(() => {
    if (!SAGE_DRAFT_KEY) return;
    if (input) localStorage.setItem(SAGE_DRAFT_KEY, input);
    else localStorage.removeItem(SAGE_DRAFT_KEY);
  }, [input, SAGE_DRAFT_KEY]);

  // Persist messages (with cached audioAll/answersAll for all 3 langs) so
  // pressing "Hear" never re-fetches audio after modal close + reopen.
  useEffect(() => {
    if (!SAGE_HISTORY_KEY) return;
    try {
      // Cap history length to avoid localStorage 5MB quota issues. Keep latest
      // ~25 message entries (each ~120KB with 3-lang audio = ~3MB max).
      const trimmed = messages.slice(-25);
      localStorage.setItem(SAGE_HISTORY_KEY, JSON.stringify(trimmed));
    } catch (e) {
      // Quota exceeded — drop audio data to keep at least the text history.
      try {
        const slim = messages.slice(-25).map(m => ({ ...m, audioAll: undefined }));
        localStorage.setItem(SAGE_HISTORY_KEY, JSON.stringify(slim));
      } catch { /* fully give up */ }
    }
  }, [messages, SAGE_HISTORY_KEY]);

  useEffect(() => {
    if (!SAGE_SESSION_KEY || !sessionId) return;
    localStorage.setItem(SAGE_SESSION_KEY, sessionId);
  }, [sessionId, SAGE_SESSION_KEY]);

  // ── KEYBOARD-AWARE PADDING ──
  // On iPad/iPhone/Android, the soft keyboard overlays the viewport without
  // resizing it. Use VisualViewport API to detect keyboard height and write
  // it to a CSS variable `--kb-h` consumed by the modal styles. This pushes
  // the input row above the keyboard so users always see what they're typing.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      const kbHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty('--kb-h', `${kbHeight}px`);
    };
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    onResize();
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
      document.documentElement.style.setProperty('--kb-h', '0px');
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 'greeting',
        sender: 'sage',
        text: 'Namaste! I am Sage, your personal Vedic astrologer. I have access to your complete birth chart and all 8 domain analyses. Ask me anything about your life.',
        timestamp: new Date(),
      }]);
    }
  }, [messages.length]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudio(null);
  };

  const playAudio = (base64Audio, msgId) => {
    stopAudio();
    if (!base64Audio) return;
    try {
      const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
      audio.onended = () => setPlayingAudio(null);
      audio.play();
      audioRef.current = audio;
      setPlayingAudio(msgId);
    } catch (e) { /* ignore audio errors */ }
  };

  const handleSend = async (questionOverride = null) => {
    const question = (questionOverride || input).trim();
    if (!question || isLoading) return;

    const userMsg = {
      id: `user_${Date.now()}`,
      sender: 'user',
      text: question,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/sage/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          question,
          language: selectedLang,
          is_followup: messages.filter(m => m.sender === 'user').length > 0,
          session_id: sessionId || `session_${userId}_${new Date().toISOString().split('T')[0]}`,
        }),
      });

      const data = await response.json();

      if (data.success) {
        if (data.session_id) setSessionId(data.session_id);
        const sageMsg = {
          id: `sage_${Date.now()}`,
          sender: 'sage',
          text: data.answer,
          answersAll: data.answers_all_languages || { [selectedLang]: data.answer },
          audioAll: data.audio_all_languages || {},
          timestamp: new Date(),
          creditsUsed: data.credits_used,
        };
        setMessages(prev => [...prev, sageMsg]);
      } else if (data.error === 'insufficient_credits') {
        setMessages(prev => [...prev, {
          id: `err_${Date.now()}`,
          sender: 'sage',
          text: `Credits insufficient. Need ${data.credits_needed}, have ${data.credits_available}. Please refill credits to continue.`,
          timestamp: new Date(),
        }]);
      } else {
        throw new Error(data.detail || 'Failed to get answer');
      }
    } catch (e) {
      setMessages(prev => [...prev, {
        id: `err_${Date.now()}`,
        sender: 'sage',
        text: `Error: ${e.message}. Please try again.`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const switchLanguage = (lang) => {
    setSelectedLang(lang);
    // Update displayed answers to switched language
    setMessages(prev => prev.map(msg => {
      if (msg.sender === 'sage' && msg.answersAll && msg.answersAll[lang]) {
        return { ...msg, text: msg.answersAll[lang] };
      }
      return msg;
    }));
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-hidden"
      style={{
        // Sage modal sits at z-[60] so it always renders above BottomNav (z-50)
        // even when the BottomNav doesn't auto-hide (older devices).
        zIndex: 60,
        // Keyboard-aware: --kb-h is set to ~0 normally, ~330px when iOS/Android
        // keyboard is up (set by visualViewport listener below). Padding-bottom
        // pushes the modal above the keyboard so the input stays visible.
        paddingBottom: 'var(--kb-h, 0px)',
        height: '100dvh',
      }}
      data-testid="sage-modal"
    >
      <div
        className="w-full max-w-2xl flex flex-col bg-stone-900 border border-stone-700 rounded-xl overflow-hidden shadow-2xl"
        style={{
          // Modal max-height shrinks when keyboard is up so messages list scrolls
          // and input row stays anchored at bottom of visible viewport.
          maxHeight: 'min(85vh, calc(100dvh - var(--kb-h, 0px) - 24px))',
        }}
      >
        {/* Header */}
        <div className="p-4 border-b border-stone-700 flex items-center justify-between bg-gradient-to-r from-stone-900 to-stone-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xl font-serif">S</div>
            <div>
              <h3 className="font-serif font-medium text-stone-100 text-sm">Sage - Vedic Astrologer</h3>
              <p className="text-xs text-stone-400">8 Domain Algorithms + Birth Chart</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Language pills */}
            <div className="flex gap-1 mr-2">
              {LANGUAGES.map(l => (
                <button
                  key={l.key}
                  onClick={() => switchLanguage(l.key)}
                  data-testid={`sage-lang-${l.key}`}
                  className={`px-2 py-0.5 rounded-full text-xs transition-all ${
                    selectedLang === l.key
                      ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                      : 'bg-stone-800 text-stone-500 border border-stone-700 hover:text-stone-300'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-stone-700 rounded-full transition-colors" data-testid="sage-close">
              <X className="w-5 h-5 text-stone-400" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="sage-messages">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] p-3 rounded-lg ${
                msg.sender === 'user'
                  ? 'bg-amber-600/30 border border-amber-600/30 text-stone-100'
                  : 'bg-stone-800 border border-stone-700 text-stone-200'
              }`}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                {/* Audio play buttons for sage messages */}
                {msg.sender === 'sage' && msg.audioAll && Object.keys(msg.audioAll).length > 0 && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-stone-700/50">
                    <span className="text-xs text-stone-500">Listen:</span>
                    {LANGUAGES.map(l => {
                      const hasAudio = msg.audioAll[l.key];
                      if (!hasAudio) return null;
                      const isPlaying = playingAudio === `${msg.id}_${l.key}`;
                      return (
                        <button
                          key={l.key}
                          onClick={() => isPlaying ? stopAudio() : playAudio(hasAudio, `${msg.id}_${l.key}`)}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-all ${
                            isPlaying ? 'bg-amber-500/30 text-amber-300' : 'bg-stone-700/50 text-stone-400 hover:text-stone-200'
                          }`}
                          data-testid={`sage-audio-${l.key}`}
                        >
                          {isPlaying ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                          {l.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-stone-800 border border-stone-700 p-3 rounded-lg flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                <span className="text-xs text-stone-400">Sage is analyzing your chart...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Questions */}
        {messages.length <= 1 && (
          <div className="px-4 pb-3">
            <p className="text-xs text-stone-500 mb-2">Quick questions:</p>
            <div className="flex flex-wrap gap-2">
              {QUICK_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => handleSend(q)}
                  data-testid="sage-quick-question"
                  className="px-3 py-1.5 bg-stone-800 border border-stone-700 hover:border-amber-600/50 hover:text-amber-300 rounded-full text-xs text-stone-400 transition-all"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-3 border-t border-stone-700 bg-stone-900">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isLoading && handleSend()}
              placeholder="Ask Sage anything about your chart..."
              disabled={isLoading}
              data-testid="sage-input"
              className="flex-1 px-4 py-2.5 rounded-lg bg-stone-800 border border-stone-700 text-stone-100 placeholder-stone-500 focus:border-amber-600/50 focus:outline-none transition-all text-sm"
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || isLoading}
              data-testid="sage-send"
              className="px-4 py-2.5 bg-amber-600/80 hover:bg-amber-600 text-white rounded-lg font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AskSageAnything;
